import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import cx from 'classnames'

import Badge from 'common/components/Badge'
import Button from 'common/components/button'
import { successToast } from 'common/components/toastNotification'
import { useAppSelector } from 'common/hooks/redux'
import { TextWithLineBreaks } from 'common/variantsUtils'
import { patchVariant } from 'features/unifiedFlow/api/api'
import { contentKeys } from 'features/unifiedFlow/api/queryKeys'
import {
  useContent,
  useSelectedComponentElement,
} from 'features/unifiedFlow/contentPage/hooks'

type ControlLine = {
  _id: string
  text: string
  sort_order: number
}

type Props = {
  onApplied: () => void
}

const AiControlLineSection = ({ onApplied }: Props) => {
  const { data: selectedElement } = useSelectedComponentElement()
  const { content } = useContent()
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const queryClient = useQueryClient()

  const subjectLines = useAppSelector(
    (state) => state.campaignStates.subjectLines
  )
  const controlLines: ControlLine[] = subjectLines.filter(
    (v) => v.reason_included === 'Control Line'
  )

  const [selectedLine, setSelectedLine] = useState<ControlLine | undefined>(
    controlLines[0]
  )
  const [appliedLineId, setAppliedLineId] = useState<string | undefined>()
  const [isSaving, setIsSaving] = useState(false)

  if (!controlLines.length) {
    return null
  }

  const selectedLineToApply = selectedLine ?? controlLines[0]

  const onApply = async () => {
    if (!selectedLineToApply || !content || !selectedElement) {
      return
    }

    setIsSaving(true)
    try {
      await patchVariant({
        accountId,
        contentId: content._id,
        elementId: selectedElement.element_id,
        variantIndex: selectedLineToApply.sort_order,
        text: selectedLineToApply.text,
        ownsl: true,
      })
      setAppliedLineId(selectedLineToApply._id)
      queryClient.invalidateQueries(contentKeys.content(content._id))
      onApplied()
      successToast('Control line applied')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h2 className="font-relais text-2xl font-semibold text-base-700">
          Control line
        </h2>
        <Badge text="Control" variant="control" size="small" />
      </div>
      <p className="text-sm text-base-700">
        Choose an AI generated control line for this workflow:
      </p>
      <div className="flex flex-col gap-3">
        {controlLines.map((line, index) => (
          <button
            key={line._id ?? index}
            type="button"
            onClick={() => setSelectedLine(line)}
            className={cx(
              'flex items-center px-4 py-3 rounded-lg border-2 text-left w-full transition-colors cursor-pointer',
              selectedLineToApply._id === line._id
                ? 'border-gray-700 bg-gray-100'
                : 'border-pink-200 bg-transparent hover:border-pink-400'
            )}
          >
            <span aria-hidden="true" className="mr-3">
              🥇
            </span>
            <span className="text-sm text-base-700">
              <TextWithLineBreaks text={line.text} />
            </span>
          </button>
        ))}
      </div>
      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={selectedLineToApply._id === appliedLineId || isSaving}
          loading={isSaving}
          onClick={onApply}
        >
          Apply
        </Button>
      </div>
    </div>
  )
}

export default AiControlLineSection
